/* How long a passage actually takes — the current triangle, and the narrows.
 *
 * Pure and headless: no fetch, no worker, no DOM, the same split every other
 * `lib/` module keeps. That is what lets the arithmetic be checked against
 * synthetic fields with known answers, which matters more here than usual —
 * every function below can be wrong in a way that produces a plausible number.
 *
 * ═══ WHAT THIS EXISTS TO FIX ═══
 *
 * `router.ts` searches a DEPTH-ONLY surface and says so: "a route through Dodd
 * Narrows reads exactly as 'safe' as one through open water by the grid's own
 * measure." In this cruising ground that makes the shortest route routinely the
 * wrong one. Porlier, Dodd, Active Pass and Gabriola run to 6-8 kn and reverse
 * every six hours; a five-knot boat that arrives at max flood does not get
 * through at all.
 *
 * So the cost of a leg here is TIME, not distance, and time depends on when you
 * start it. Two things follow, and they are the whole module:
 *
 *   THE CURRENT TRIANGLE. Speed made good is not boat speed plus current speed;
 *   it is the along-track component of the vector sum, once the boat has been
 *   turned far enough upstream to hold the track at all.
 *
 *   THE NARROWS ARE GATES. A pass has slack times, and arriving outside the
 *   window around one costs you the wait until the next. That wait is a real
 *   part of the passage and is reported as one, never hidden inside a number.
 *
 * ═══ ADVISORY, LIKE EVERYTHING ELSE THAT TOUCHES THIS DATA ═══
 *
 * `CHS_NOTICE` in autoroute.svelte.ts is carried by every caller and this
 * changes nothing about that. A schedule that names a slack to the minute
 * invites more trust than a line on a chart does, which makes the framing more
 * important here, not less. Nothing in this file may be read as a guarantee
 * that a pass is transitable at a stated time.
 */

import { bearingDeg, rangeM } from './chartpick.ts';
import type { LatLon } from './router.ts';

const MS_PER_H = 3_600_000;
const M_PER_NM = 1852;

/** Half-width of the window around a slack that counts as "at slack".
 *
 *  One hour each side, which is what was asked for and is also roughly the
 *  usable window at the Gulf Islands passes: Porlier and Dodd are workable for
 *  a displacement boat for about that long either side, and Active Pass is
 *  shorter but is dominated by ferry traffic rather than by rate. NOT a claim
 *  about any particular pass — a caller may narrow it, and the sheet says what
 *  window was used. */
export const SLACK_WINDOW_MS = 60 * 60_000;

/** A narrows the router must time, built from a CHS tidal-stream station.
 *
 *  `slacks` comes from the shipped station events, which cover the whole year —
 *  unlike the model current field, which is a rolling window of a day or two.
 *  That asymmetry is the reason gates keep working for a departure next week
 *  when surface-current optimisation cannot. */
export interface Gate {
  code: string;
  name: string;
  lat: number;
  lon: number;
  /** How close to the station counts as being in the pass. */
  radiusM: number;
  /** Epoch ms of every slack in the shipped data, ascending. */
  slacks: readonly number[];
}

/** The current at a place and an instant, or null where nothing is known.
 *
 *  NULL IS NOT ZERO. "No data here" and "slack water here" are different facts
 *  and the second one is a claim. A null makes the leg fall back to boat speed
 *  alone and sets `currentsUsed` false on the schedule, so the panel can say the
 *  route was not current-optimised rather than implying it was. */
export type CurrentSampler = (
  lat: number,
  lon: number,
  t: number
) => { /** Degrees true the water is setting TOWARD. */ setToDeg: number; kn: number } | null;

/** The fields a timed search needs beyond `PassageModel` — see router.ts's
 *  `TimedModel`. Named here so a test can build one without importing the
 *  search. */
export type TimedModelSeed = PassageModel & { ceilingKn: number };

export interface PassageModel {
  /** Speed through the water, knots. The boat's own speed, not SOG. */
  boatKn: number;
  current: CurrentSampler;
  gates: readonly Gate[];
  /** Defaults to SLACK_WINDOW_MS. */
  slackWindowMs?: number;
}

/* ═══ THE CURRENT TRIANGLE ═══ */

const rad = (d: number) => (d * Math.PI) / 180;

/**
 * Speed made good along `trackDeg`, in knots, for a boat that turns as far
 * upstream as it must to hold that track.
 *
 * The maths, stated because it is easy to write something plausible and wrong:
 * decompose the current into components along the track (`along`) and across it
 * (`cross`). To make good the track, the boat's own velocity must cancel
 * `cross` exactly, which costs it `sqrt(boat² - cross²)` of forward speed. What
 * is left, plus whatever the current contributes along the track, is SOG.
 *
 *   sog = sqrt(boat² - cross²) + along
 *
 * ═══ TWO WAYS THIS RETURNS "IMPOSSIBLE" ═══
 *
 * `cross` exceeding boat speed means the boat cannot stop being swept sideways
 * and simply cannot hold the track — the square root would go imaginary. And a
 * `sog` at or below zero means it is being pushed backwards faster than it can
 * drive forwards. Both are real answers about a real boat in a real pass, and
 * both return null rather than a very large number: a leg that takes eleven
 * hours because the maths was clamped is a lie that looks like a plan.
 */
export function sogKn(trackDeg: number, boatKn: number, cur: { setToDeg: number; kn: number } | null): number | null {
  if (!(boatKn > 0)) return null;
  if (!cur || cur.kn === 0) return boatKn;
  /* Angle from the track to where the water is going. 0 = fair, 180 = foul. */
  const off = rad(cur.setToDeg - trackDeg);
  const along = cur.kn * Math.cos(off);
  const cross = cur.kn * Math.sin(off);
  const drive = boatKn * boatKn - cross * cross;
  if (drive <= 0) return null; // swept sideways faster than it can steer
  const sog = Math.sqrt(drive) + along;
  return sog > 0 ? sog : null; // pushed backwards
}

/**
 * Seconds to cross one leg, starting at `startT`.
 *
 * The current is sampled ONCE per leg, at its midpoint and at the start time,
 * rather than integrated along it. That is a deliberate approximation and it is
 * good where it is used: the router's cells are ~153 m apart and the model
 * field is a ~500 m grid on the hour, so sub-leg variation is below the
 * resolution of the data. `scheduleFor` re-samples per leg, so a long passage
 * still follows the turn of the tide.
 *
 * ═══ AND IT IS WHAT BOUNDS THE SEARCH'S CORRECTNESS ═══
 *
 * Measured over a 12-hour sweep at one-minute departures against a smooth
 * six-hour reversing tide (`passage.test.ts` pins both numbers):
 *
 *     0.1 nm legs (~72 s, the router's cell scale)   0.00 min FIFO violation
 *     6 nm legs   (spanning a tide turn)             1.53 min
 *
 * A* over time-dependent costs is optimal only where departing later cannot
 * arrive earlier, and per-leg sampling is the only thing in this file that can
 * break that. At cell scale it does not, which is why the search needs no
 * special machinery. Over miles it does — by a little — which is why a gate's
 * wait is modelled EXPLICITLY rather than left for the cost function to find.
 *
 * Returns null when the leg cannot be made good — see `sogKn`.
 */
export function legSeconds(from: LatLon, to: LatLon, startT: number, m: PassageModel): number | null {
  const distM = rangeM(from, to);
  if (distM === 0) return 0;
  const mid = { lat: (from.lat + to.lat) / 2, lon: (from.lon + to.lon) / 2 };
  const sog = sogKn(bearingDeg(from, to), m.boatKn, m.current(mid.lat, mid.lon, startT));
  if (sog === null) return null;
  return (distM / M_PER_NM / sog) * 3600;
}

/* ═══ THE GATES ═══ */

/** Where a leg passes closest to a gate: the fraction along it, the position,
 *  and how far off that is. */
export interface Approach {
  gate: Gate;
  /** 0 at `from`, 1 at `to`. */
  f: number;
  at: LatLon;
  distM: number;
}

/**
 * The gates a LEG passes through, in the order it meets them.
 *
 * ═══ PER LEG, NOT PER WAYPOINT ═══
 *
 * The first version tested whether a waypoint fell inside a gate, and that is
 * not the same question. `simplify()` puts waypoints at turns; a route can run
 * straight through Dodd Narrows with its nearest waypoint a mile either side,
 * and every gate check would miss it. A route that sails through a pass at max
 * flood while reporting no constraint is the exact failure this feature exists
 * to prevent, so the test has to be about the track, not its corners.
 */
export function gatesOnLeg(from: LatLon, to: LatLon, gates: readonly Gate[]): Approach[] {
  const out: Approach[] = [];
  for (const g of gates) {
    const a = closestApproach(from, to, g);
    if (a.distM <= g.radiusM) out.push({ gate: g, ...a });
  }
  return out.sort((x, y) => x.f - y.f);
}

/** Closest point on the segment `from`→`to` to `p`, as a fraction along it.
 *
 *  Flat-earth in metres, like `rangeM` and `distanceToSegmentKm` beside it —
 *  over a leg of a few miles at these latitudes the error is far below a gate
 *  radius, and mixing a spherical solve into a file of flat ones would be
 *  a worse kind of wrong. */
export function closestApproach(
  from: LatLon,
  to: LatLon,
  p: LatLon
): { f: number; at: LatLon; distM: number } {
  const cosLat = Math.cos(((from.lat + to.lat) / 2) * (Math.PI / 180));
  const x = (q: LatLon) => q.lon * 111_320 * cosLat;
  const y = (q: LatLon) => q.lat * 111_320;
  const ax = x(from);
  const ay = y(from);
  const bx = x(to) - ax;
  const by = y(to) - ay;
  const len2 = bx * bx + by * by;
  const f = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((x(p) - ax) * bx + (y(p) - ay) * by) / len2));
  const at = { lat: from.lat + f * (to.lat - from.lat), lon: from.lon + f * (to.lon - from.lon) };
  return { f, at, distM: rangeM(at, p) };
}

/**
 * When a boat arriving at `t` may enter this gate.
 *
 * Inside a window already: `t`, no wait. Otherwise the start of the next
 * window, which is `nextSlack - window`. Null when the shipped data holds no
 * further slack — past the end of the year the honest answer is "not known",
 * not "any time".
 *
 * ═══ WHY WAITING IS THE MECHANISM AND NOT A SPECIAL CASE ═══
 *
 * Because waiting is always allowed, leaving later can never get you there
 * earlier — the FIFO property. That is what makes a plain A* correct over these
 * time-dependent costs, so the search needs no new machinery: the wait is just
 * part of what the edge costs. `passage.test.ts` asserts FIFO directly, since
 * the search's correctness rests on it.
 */
export function openAt(gate: Gate, t: number, windowMs = SLACK_WINDOW_MS): number | null {
  /* ═══ BINARY SEARCH, BECAUSE THIS RUNS INSIDE A* ═══
   *
   * A station ships ~1410 slacks for the year, so a linear scan from January
   * costs ~900 comparisons for an August departure — per gate, per edge, over
   * thousands of expanded cells. The array is ascending (`gatesFromStations`
   * sorts it and says why), so the first slack whose window has not yet closed
   * is a lower-bound search. */
  const s = gate.slacks;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (s[mid]! + windowMs < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo >= s.length) return null;
  return Math.max(t, s[lo]! - windowMs);
}

/* ═══ THE SCHEDULE ═══ */

export interface ScheduleLeg {
  from: LatLon;
  to: LatLon;
  startT: number;
  endT: number;
  /** Speed made good, knots. */
  sogKn: number;
  /** The current used, or null where none was known. */
  current: { setToDeg: number; kn: number } | null;
}

export interface ScheduleWait {
  /** Index of the LEG the wait happens on — the boat is somewhere along it,
   *  not at either end. */
  atIndex: number;
  /** Where it holds: the point on the leg closest to the pass. */
  at: LatLon;
  from: number;
  untilT: number;
  gate: Gate;
}

export interface Schedule {
  departT: number;
  arriveT: number;
  legs: ScheduleLeg[];
  waits: ScheduleWait[];
  /** Arrival time at each point, so a strip can label every waypoint. Same
   *  length as the point list. */
  etaAt: number[];
  /**
   * False when NO leg had a current to work with — the model field does not
   * reach this departure or this water, so the route was timed on boat speed
   * and the gates alone.
   *
   * The panel must say so. Silence here would let a schedule that never saw a
   * current read exactly like one that did.
   */
  currentsUsed: boolean;
  /** Set when a leg could not be made good at this speed. The schedule up to
   *  that point is still returned — a partial answer that says where it stopped
   *  beats no answer. */
  impossibleAt: number | null;
  /** Set when a gate has no further slack in the shipped data. */
  unknownGate: Gate | null;
}

/**
 * Forward-integrate a finished point list into a schedule.
 *
 * Used twice over, which is the point of it being pure: the search calls the
 * same physics through `legSeconds` while it explores, and the UI calls this to
 * label the route the search returned. One implementation means the strip
 * cannot disagree with the thing that chose the route — a class of bug that
 * would be invisible and very hard to argue with.
 *
 * `legs` is therefore NOT one entry per waypoint pair: a leg that passes a gate
 * is split at the pass, so the schedule can show the run up to it, the wait, and
 * the run through. `etaAt` stays one entry per waypoint.
 */
export function scheduleFor(pts: readonly LatLon[], departT: number, m: PassageModel): Schedule {
  const windowMs = m.slackWindowMs ?? SLACK_WINDOW_MS;
  const legs: ScheduleLeg[] = [];
  const waits: ScheduleWait[] = [];
  const etaAt: number[] = [departT];
  let t = departT;
  let currentsUsed = false;
  let impossibleAt: number | null = null;
  let unknownGate: Gate | null = null;

  /** One stretch of water at one time. Returns false when it cannot be made
   *  good, so the caller can stop and say where. */
  const run = (a: LatLon, b: LatLon): boolean => {
    const distM = rangeM(a, b);
    if (distM === 0) return true;
    const mid = { lat: (a.lat + b.lat) / 2, lon: (a.lon + b.lon) / 2 };
    const cur = m.current(mid.lat, mid.lon, t);
    if (cur) currentsUsed = true;
    const secs = legSeconds(a, b, t, m);
    if (secs === null) return false;
    const endT = t + secs * 1000;
    legs.push({
      from: a,
      to: b,
      startT: t,
      endT,
      sogKn: secs > 0 ? (distM / M_PER_NM / secs) * 3600 : m.boatKn,
      current: cur
    });
    t = endT;
    return true;
  };

  outer: for (let i = 0; i < pts.length - 1; i++) {
    const from = pts[i]!;
    const to = pts[i + 1]!;

    /* ═══ SAIL TO THE PASS, WAIT THERE, THEN GO THROUGH ═══
     *
     * The leg is SPLIT at each gate it passes through rather than gated at its
     * endpoints. Two earlier versions got this wrong in opposite directions:
     * holding the boat at the previous waypoint made it wait an hour longer
     * than it needed to, and holding it at the next one let it transit the pass
     * before the wait it was waiting for.
     *
     * Splitting is also what makes the post-wait water honest — the second half
     * is timed with the current at the time the boat actually sets off again,
     * which after a two-hour hold is a different tide entirely. */
    let cursor = from;
    for (const ap of gatesOnLeg(from, to, m.gates)) {
      if (!run(cursor, ap.at)) {
        impossibleAt = i;
        break outer;
      }
      cursor = ap.at;

      const open = openAt(ap.gate, t, windowMs);
      if (open === null) {
        unknownGate = ap.gate;
        continue;
      }
      if (open > t) {
        waits.push({ atIndex: i, at: ap.at, from: t, untilT: open, gate: ap.gate });
        t = open;
      }
    }

    if (!run(cursor, to)) {
      impossibleAt = i;
      break;
    }
    etaAt.push(t);
  }

  return {
    departT,
    arriveT: t,
    legs,
    waits,
    etaAt,
    currentsUsed,
    impossibleAt,
    unknownGate
  };
}

/** Total time under way, excluding waits — for a strip that wants to show both
 *  "6h 20m under way, 35m waiting" rather than one number that hides the wait. */
export function underwayMs(s: Schedule): number {
  return s.legs.reduce((n, l) => n + (l.endT - l.startT), 0);
}

/** Total time waiting for slack. */
export function waitingMs(s: Schedule): number {
  return s.waits.reduce((n, w) => n + (w.untilT - w.from), 0);
}

/** The fastest a boat could possibly go anywhere on this passage, knots — the
 *  admissible bound the search's heuristic needs. Never underestimates, so the
 *  heuristic never overestimates the remaining time and A* stays optimal. */
export function maxSpeedKn(boatKn: number, maxCurrentKn: number): number {
  return boatKn + Math.max(0, maxCurrentKn);
}

/** Hours the model field is asked for, for a passage of `distM` at `boatKn`
 *  starting at `departT` — what the worker needs posted to it.
 *
 *  Padded by half again because a current-aware route is longer in time than a
 *  straight-line estimate whenever it waits, and an hour missing off the end
 *  silently turns into "no current known" for the last leg. */
export function hoursNeeded(departT: number, distM: number, boatKn: number): [number, number] {
  const straightH = distM / M_PER_NM / Math.max(0.5, boatKn);
  const spanH = Math.max(1, straightH * 1.5);
  const from = Math.floor(departT / MS_PER_H) * MS_PER_H;
  return [from, from + Math.ceil(spanH) * MS_PER_H];
}
